"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { useCart } from "@/hooks/use-cart";
import { Button } from "@/components/ui/button";
import { X, ShoppingCart } from "lucide-react";
import CartItem from "./cart-item";
import { useClickOutside } from "@/hooks/use-click-outside";

interface CartProps {
  open: boolean;
  onClose: () => void;
}

export default function Cart({ open, onClose }: CartProps) {
  const { items } = useCart();
  const [mounted, setMounted] = useState(false);
  const cartRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  
  // Use the click outside hook
  useClickOutside(cartRef, onClose, [closeButtonRef]);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  // Group items by id and variant_id and count quantities
  const groupedItems = items.reduce<Record<string, { item: any; quantity: number }>>(
    (acc, item) => {
      const key = `${item.id}-${item.variant_id || 'default'}`;
      
      if (!acc[key]) {
        acc[key] = { item, quantity: item.quantity || 1 };
      } else {
        acc[key] = { item, quantity: acc[key].quantity + (item.quantity || 1) };
      }
      return acc;
    },
    {}
  );

  const cartItems = Object.values(groupedItems);
  
  const subtotal = items.reduce((total, item) => total + (item.price * (item.quantity || 1)), 0);
  
  const formattedSubtotal = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(subtotal);

  if (!mounted) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          <motion.div
            ref={cartRef}
            className="fixed right-0 top-0 h-[100dvh] w-full sm:w-[400px] bg-background border-l z-50"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 20 }}
          >
            <div className="flex flex-col h-full">
              <div className="py-4 px-6 border-b flex items-center justify-between">
                <div className="flex items-center">
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  <h2 className="text-lg font-bold">Your Cart</h2>
                </div>
                <Button 
                  ref={closeButtonRef}
                  variant="ghost" 
                  size="icon" 
                  onClick={onClose}
                  aria-label="Close cart"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-6">
                    <ShoppingCart className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="font-medium text-xl mb-2">Your cart is empty</h3>
                    <p className="text-muted-foreground mb-4">Add some products to your cart to see them here.</p>
                    <Button onClick={onClose}>Continue Shopping</Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {cartItems.map(({ item, quantity }) => (
                      <CartItem key={`${item.id}-${item.variant_id || 'default'}`} item={item} quantity={quantity} />
                    ))}
                  </div>
                )}
              </div>

              {cartItems.length > 0 && (
                <div className="border-t p-6">
                  <div className="flex justify-between mb-4">
                    <span className="font-medium">Subtotal</span>
                    <span className="font-bold">{formattedSubtotal}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Shipping and taxes calculated at checkout
                  </p>
                  <Button asChild className="w-full mb-2">
                    <Link href="/checkout" onClick={onClose}>Checkout</Link>
                  </Button>
                  <Button variant="outline" onClick={onClose} className="w-full">
                    Continue Shopping
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}